import { fontManager } from './fontManager';
import { calculateOptimalFontSize, wrapText } from './index';
import { applyFontVariationToCanvas } from './variableFontUtils';

/** When a positive fixed font size is provided we render it verbatim (honoring
 *  the user's choice). Otherwise we auto-fit the text into the box. */
export function shouldAutoFitFontSize(fontSize?: number | null): boolean {
  return !(typeof fontSize === 'number' && fontSize > 0);
}

interface ExportOptions {
  backgroundColor: string;
  useGradient?: boolean;
  backgroundColorEnd?: string;
  backgroundAlpha?: number;
  backgroundAlphaEnd?: number;
  textColor: string;
  useTwoColors?: boolean;
  textColorSecondary?: string;
  fontFamily: string;
  fontUrl?: string;
  fontName?: string;
  fontSize?: number;
  logoUrl?: string;
  logoPosition?: 'top-left' | 'top-center' | 'top-right' | 'bottom-left' | 'bottom-right';
  logoSize?: number;
  textLineSpacing?: number;
  imageUrl?: string;
  text?: string;
  textUppercase?: boolean;
  fontBold?: boolean;
  fontVariationSettings?: Record<string, number>;
  fitMode?: 'fill' | 'fit';
  showLine?: boolean;
  lineColor?: string;
  lineHeight?: number;
  linePosition?: 'top' | 'bottom';
  sourceWidth?: number;
}

/**
 * Конвертирует HEX в RGBA
 */
function hexToRgba(hex: string, alpha: number): string {
  hex = hex.replace('#', '');
  const r = parseInt(hex.substring(0, 2), 16);
  const g = parseInt(hex.substring(2, 4), 16);
  const b = parseInt(hex.substring(4, 6), 16);
  return `rgba(${r}, ${g}, ${b}, ${alpha})`;
}

/**
 * Загружает изображение
 */
function loadImage(src: string): Promise<HTMLImageElement> {
  return new Promise((resolve, reject) => {
    const img = new Image();
    img.crossOrigin = 'anonymous';
    img.onload = () => resolve(img);
    img.onerror = reject;
    img.src = src;
  });
}

/**
 * Экспортирует шаблон в blob для последующей загрузки
 * Используется в features для экспорта постов и рилсов
 */
export async function exportTemplateToBlob(options: ExportOptions): Promise<Blob> {
  const {
    backgroundColor,
    useGradient,
    backgroundColorEnd,
    backgroundAlpha,
    backgroundAlphaEnd,
    textColor,
    useTwoColors,
    textColorSecondary,
    fontFamily,
    fontUrl,
    fontName,
    fontSize,
    logoUrl,
    logoPosition = 'top-left',
    imageUrl,
    text = 'Пример текста поста',
    textUppercase = false,
    fontBold = false,
    fontVariationSettings,
    fitMode = 'fill',
    showLine = false,
    lineColor = '#000000',
    lineHeight = 2,
    linePosition = 'top',
    sourceWidth,
  } = options;

  try {
    // Гарантируем загрузку шрифта перед экспортом
    let actualFontFamily = fontFamily;
    if (fontUrl && fontName) {
      console.log('🔤 Гарантируем загрузку шрифта перед экспортом:', fontName);
      actualFontFamily = await fontManager.ensureFontLoaded(fontUrl, fontName);
      console.log('✅ Шрифт загружен для экспорта:', actualFontFamily);
    }

    // Ждём загрузки шрифтов
    await document.fonts.ready;
    await new Promise(resolve => setTimeout(resolve, 50));

    // Вычисляем масштаб на основе реальной ширины PostTemplate
    const realSourceWidth = sourceWidth || 400;
    const scale = 1080 / realSourceWidth;
    
    console.log('🎨 Export scale:', { realSourceWidth, scale });

    // Создаём canvas с точными размерами Instagram поста
    const canvas = document.createElement('canvas');
    canvas.width = 1080;
    canvas.height = 1350;
    
    const ctx = canvas.getContext('2d', {
      alpha: false,
      colorSpace: 'srgb',
    });

    if (!ctx) {
      throw new Error('Could not get canvas context');
    }

    // 1. Заливаем белый фон
    ctx.fillStyle = '#ffffff';
    ctx.fillRect(0, 0, 1080, 1350);

    // 2. Рисуем основное изображение
    const imageHeight = useGradient ? 1350 : 1080;
    
    if (imageUrl) {
      const img = await loadImage(imageUrl);
      
      const imgAspect = img.width / img.height;
      const canvasAspect = 1080 / imageHeight;
      
      if (fitMode === 'fit') {
        // Режим FIT: сначала рисуем размытый фон (cover), потом основное изображение (contain)
        
        // 1. Размытый фон (cover) с scale(1.1) как в превью
        let coverWidth, coverHeight, coverOffsetX, coverOffsetY;
        if (imgAspect > canvasAspect) {
          coverHeight = imageHeight;
          coverWidth = coverHeight * imgAspect;
          coverOffsetX = (1080 - coverWidth) / 2;
          coverOffsetY = 0;
        } else {
          coverWidth = 1080;
          coverHeight = coverWidth / imgAspect;
          coverOffsetX = 0;
          coverOffsetY = (imageHeight - coverHeight) / 2;
        }
        
        // Применяем scale(1.1) - увеличиваем на 10% как в превью
        const scaleFactor = 1.1;
        const scaledWidth = coverWidth * scaleFactor;
        const scaledHeight = coverHeight * scaleFactor;
        const scaledOffsetX = coverOffsetX - (scaledWidth - coverWidth) / 2;
        const scaledOffsetY = coverOffsetY - (scaledHeight - coverHeight) / 2;
        
        // Рисуем размытый фон
        ctx.filter = 'blur(20px)';
        ctx.drawImage(img, scaledOffsetX, scaledOffsetY, scaledWidth, scaledHeight);
        ctx.filter = 'none';
        
        // 2. Основное изображение (contain) поверх размытого
        let fitWidth, fitHeight, fitOffsetX, fitOffsetY;
        if (imgAspect > canvasAspect) {
          fitWidth = 1080;
          fitHeight = fitWidth / imgAspect;
          fitOffsetX = 0;
          fitOffsetY = (imageHeight - fitHeight) / 2;
        } else {
          fitHeight = imageHeight;
          fitWidth = fitHeight * imgAspect;
          fitOffsetX = (1080 - fitWidth) / 2;
          fitOffsetY = 0;
        }
        
        ctx.drawImage(img, fitOffsetX, fitOffsetY, fitWidth, fitHeight);
        
      } else {
        // Режим FILL: обычный cover
        let drawWidth, drawHeight, offsetX, offsetY;
        
        if (imgAspect > canvasAspect) {
          drawHeight = imageHeight;
          drawWidth = drawHeight * imgAspect;
          offsetX = (1080 - drawWidth) / 2;
          offsetY = 0;
        } else {
          drawWidth = 1080;
          drawHeight = drawWidth / imgAspect;
          offsetX = 0;
          offsetY = (imageHeight - drawHeight) / 2;
        }
        
        ctx.drawImage(img, offsetX, offsetY, drawWidth, drawHeight);
      }
    }

    // 3. Рисуем логотип в верхней части (если позиция top-*)
    // Per-account square logo box side (canvas px). When unset, keep the original
    // per-position defaults (top 64*scale, bottom 48*scale) so existing templates
    // are unchanged; when set, both positions use the configured size.
    const logoOverride = options.logoSize && options.logoSize > 0 ? options.logoSize : null;
    const logoSizeTop = logoOverride ?? 64 * scale;
    const logoSizeBottom = logoOverride ?? 48 * scale;
    const logoPadding = 16 * scale;
    
    if (logoUrl && logoPosition && !logoPosition.startsWith('bottom')) {
      try {
        const logoImg = await loadImage(logoUrl);
        
        console.log('🖼️ Logo:', { logoSizeTop, logoPadding, logoPosition });
        
        let logoX, logoY;
        switch (logoPosition) {
          case 'top-left':
            logoX = logoPadding;
            logoY = logoPadding;
            break;
          case 'top-center':
            logoX = 540 - logoSizeTop / 2;
            logoY = logoPadding;
            break;
          case 'top-right':
            logoX = 1080 - logoSizeTop - logoPadding;
            logoY = logoPadding;
            break;
        }
        
        ctx.drawImage(logoImg, logoX, logoY, logoSizeTop, logoSizeTop);
      } catch (error) {
        console.warn('Failed to load logo:', error);
      }
    }

    // 4. Рисуем текстовый блок (20% высоты = 270px)
    const textBlockY = 1080;
    const textBlockHeight = 270;
    
    if (useGradient) {
      // Градиент СНИЗУ ВВЕРХ (как в превью: to top)
      const gradient = ctx.createLinearGradient(0, textBlockY + textBlockHeight, 0, textBlockY);
      gradient.addColorStop(0, hexToRgba(backgroundColor, backgroundAlpha ?? 1));
      gradient.addColorStop(1, hexToRgba(backgroundColorEnd || backgroundColor, backgroundAlphaEnd ?? backgroundAlpha ?? 1));
      ctx.fillStyle = gradient;
    } else {
      // Без градиента - просто цвет без прозрачности
      ctx.fillStyle = backgroundColor;
    }
    ctx.fillRect(0, textBlockY, 1080, textBlockHeight);

    // 5. Рисуем логотип в нижней части (если позиция bottom-*)
    let logoImg: HTMLImageElement | null = null;
    if (logoUrl && logoPosition && logoPosition.startsWith('bottom')) {
      try {
        logoImg = await loadImage(logoUrl);
        
        const gapSize = 12 * scale;
        let logoX, logoY;
        
        switch (logoPosition) {
          case 'bottom-left':
            logoX = logoPadding;
            logoY = textBlockY + (textBlockHeight - logoSizeBottom) / 2;
            break;
          case 'bottom-right':
            logoX = 1080 - logoSizeBottom - logoPadding;
            logoY = textBlockY + (textBlockHeight - logoSizeBottom) / 2;
            break;
        }
        
        ctx.drawImage(logoImg, logoX, logoY, logoSizeBottom, logoSizeBottom);
      } catch (error) {
        console.warn('Failed to load logo in text area:', error);
        logoImg = null;
      }
    }

    // 6. Рисуем текст
    if (text && text.trim()) {
      const paddingX = 16 * scale;
      const gapSize = 12 * scale;
      
      const hasLogoInBottom = logoImg && logoPosition && logoPosition.startsWith('bottom');
      const logoWidthWithGap = hasLogoInBottom ? logoSizeBottom + gapSize : 0;
      
      const maxWidth = (1080 - (paddingX * 2) - logoWidthWithGap) * 0.97;
      
      const processedText = textUppercase ? text.toUpperCase() : text;
      // Фикс-размер чести verbatim (как exportTemplate.ts); иначе авто-фит под бокс.
      let optimalFontSize: number;
      let lines: string[];
      if (shouldAutoFitFontSize(fontSize)) {
        ({ fontSize: optimalFontSize, lines } = calculateOptimalFontSize(
          processedText,
          maxWidth,
          textBlockHeight * 0.8,
          actualFontFamily,
          ctx,
          20 * scale,
        ));
      } else {
        optimalFontSize = (fontSize as number) * scale;
        ctx.font = `${optimalFontSize}px "${actualFontFamily}"`;
        lines = wrapText(processedText, maxWidth, optimalFontSize, actualFontFamily, ctx);
      }
      
      // Применяем variable font настройки или fallback на fontBold
      applyFontVariationToCanvas(ctx, optimalFontSize, actualFontFamily, fontVariationSettings, fontBold);
      ctx.fillStyle = textColor;
      ctx.textAlign = 'center';
      ctx.textBaseline = 'middle';
      
      let textX = 540;
      
      if (logoPosition === 'bottom-left') {
        textX = 540 + (logoSizeBottom + gapSize) / 2;
      } else if (logoPosition === 'bottom-right') {
        textX = 540 - (logoSizeBottom + gapSize) / 2;
      }
      
      console.log('📝 Text rendering:', { 
        optimalFontSize, 
        linesCount: lines.length, 
        maxWidth, 
        hasLogoInBottom, 
        textX, 
        logoPosition 
      });
      
      const textY = textBlockY + textBlockHeight / 2;
      const lineSpacingOpt = options.textLineSpacing && options.textLineSpacing > 0 ? options.textLineSpacing : 1.2;
      const textLineHeight = optimalFontSize * lineSpacingOpt;
      
      const totalHeight = lines.length * textLineHeight;
      const startY = textY - totalHeight / 2 + textLineHeight / 2;

      lines.forEach((line, i) => {
        const lineY = startY + i * textLineHeight;
        if (useTwoColors && textColorSecondary) {
          ctx.fillStyle = i === 0 ? textColor : textColorSecondary;
        }
        if (line.trim()) {
          ctx.fillText(line, textX, lineY);
        }
      });
    }

    // 7. Рисуем декоративную линию
    if (showLine) {
      ctx.fillStyle = lineColor;
      const scaledLineHeight = lineHeight * scale;
      const lineY = linePosition === 'top' ? textBlockY : textBlockY + textBlockHeight - scaledLineHeight;
      ctx.fillRect(0, lineY, 1080, scaledLineHeight);
    }

    // 8. Конвертируем в blob
    return new Promise((resolve, reject) => {
      canvas.toBlob((blob) => {
        if (!blob) {
          reject(new Error('Failed to create blob from canvas'));
          return;
        }
        resolve(blob);
      }, 'image/png', 1.0);
    });
  } catch (error) {
    console.error('❌ Error exporting template to blob:', error);
    throw error;
  }
}